import { useEffect, useRef, useState, type FormEvent, type PointerEvent as ReactPointerEvent } from "react";
import { downloadPodEvidence, getPod, reviewPod, sendPod, submitPod, uploadPodEvidence, type LoadPod } from "./api";
import { useRealtime } from "./realtime";
import{getOfflineQueueState}from"./offline";

export function PodPanel({ loadId, companyId, canManage }: { loadId: string; companyId: string; canManage: boolean }) {
  const realtime = useRealtime();
  const [pod, setPod] = useState<LoadPod | null>(null);
  const [recipient, setRecipient] = useState("");
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState<string>();
  const [paperwork, setPaperwork] = useState<File[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [queued,setQueued]=useState(false);

  async function refresh() {
    const result = await getPod(loadId, companyId);
    setPod(result.pod);
  }

  useEffect(() => { void refresh().catch(() => undefined); }, [loadId]);
  useEffect(() => {
    if (realtime.latest?.type === "notification.changed" || realtime.latest?.type === "pod.changed") {
      void refresh().catch(() => undefined);
    }
  }, [realtime.latest?.id]);
  useEffect(() => {
    if (new URLSearchParams(location.search).get("pod") === loadId) {
      document.getElementById("pod-" + loadId)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await submitPod(loadId, {
        companyId,
        recipientName: recipient,
        ...(notes.trim() ? { notes } : {}),
        ...(signature ? { signatureData: signature } : {})
      });
      for (const file of paperwork) await uploadPodEvidence(loadId, companyId, "paperwork", file);
      for (const file of photos) await uploadPodEvidence(loadId, companyId, "photo", file);
      const offline=await getOfflineQueueState();if(offline.pending){setQueued(true);setRecipient("");setNotes("");setSignature(undefined);setPaperwork([]);setPhotos([]);}else await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "POD could not be submitted.");
    } finally {
      setBusy(false);
    }
  }

  async function act(action: "approve" | "changes_requested" | "send") {
    setBusy(true);
    setError("");
    try {
      if (action === "send") {
        setPod(await sendPod(loadId, companyId));
      } else {
        const reason = action === "changes_requested" ? window.prompt("What must the driver correct?") ?? "" : undefined;
        if (action === "changes_requested" && !reason?.trim()) return;
        setPod(await reviewPod(loadId, companyId, action, reason));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "POD action failed.");
    } finally {
      setBusy(false);
    }
  }

  const editing = !pod || pod.status === "changes_requested";
  return (
    <section className="pod-panel" id={"pod-" + loadId}>
      <header>
        <div><strong>Proof of delivery</strong><small>{pod ? label(pod.status) : "Not submitted"}</small></div>
        {pod?.status === "awaiting_review" && <b>Action required</b>}
      </header>
      {error && <div className="alert alert-danger py-2">{error}</div>}{queued&&<div className="alert alert-warning py-2">POD saved safely on this device and queued for sync.</div>}
      {editing ? (
        <form onSubmit={submit}>
          <div className="pod-fields">
            <label><span>Recipient name</span><input required maxLength={200} className="form-control" value={recipient} onChange={(event) => setRecipient(event.target.value)} /></label>
            <label><span>Notes</span><textarea className="form-control" value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
          </div>
          <Signature value={signature} changed={setSignature} />
          <div className="pod-files">
            <label><span>Signed paperwork (signature or paperwork required)</span><input type="file" multiple accept="image/jpeg,image/png,application/pdf" onChange={(event) => setPaperwork(Array.from(event.target.files ?? []))} /></label>
            <label><span>Delivery photographs</span><input type="file" multiple accept="image/jpeg,image/png,image/heic,image/heif" onChange={(event) => setPhotos(Array.from(event.target.files ?? []))} /></label>
          </div>
          <button className="btn btn-primary" disabled={busy}>{busy ? "Submitting…" : "Submit POD"}</button>
        </form>
      ) : (
        <>
          <dl className="pod-summary">
            <div><dt>Recipient</dt><dd>{pod.recipientName}</dd></div>
            <div><dt>Submitted</dt><dd>{new Date(pod.submittedAt).toLocaleString("en-GB")}</dd></div>
            {pod.notes && <div><dt>Notes</dt><dd>{pod.notes}</dd></div>}
            <div><dt>Signature</dt><dd>{pod.signatureData ? <img className="pod-signature-preview" src={pod.signatureData} alt="Recipient signature" /> : "Not captured"}</dd></div>
          </dl>
          <div className="pod-evidence">
            {pod.evidence.map((file) => <button type="button" key={file.id} onClick={() => void downloadPodEvidence(loadId, companyId, file.id)}>{file.kind.replace("_", " ")} · {file.filename}</button>)}
          </div>
          {canManage && pod.status === "awaiting_review" && (
            <div className="pod-actions">
              <button className="btn btn-success" disabled={busy} onClick={() => void act("approve")}>Approve POD</button>
              <button className="btn btn-outline-warning" disabled={busy} onClick={() => void act("changes_requested")}>Return for changes</button>
            </div>
          )}
          {canManage && pod.status === "approved" && <button className="btn btn-primary" disabled={busy} onClick={() => void act("send")}>{busy ? "Sending…" : "Send POD to poster"}</button>}
          <details><summary>Audit history ({pod.history.length})</summary>{pod.history.map((item) => <p key={item.id}><strong>{item.action.replaceAll("_", " ")}</strong> · {item.actorEmail} · {new Date(item.createdAt).toLocaleString("en-GB")}</p>)}</details>
        </>
      )}
    </section>
  );
}

function Signature({ value, changed }: { value: string | undefined; changed: (value: string | undefined) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  function point(event: ReactPointerEvent<HTMLCanvasElement>) { const canvas = ref.current!; const rect = canvas.getBoundingClientRect(); return { x: (event.clientX - rect.left) * canvas.width / rect.width, y: (event.clientY - rect.top) * canvas.height / rect.height }; }
  function start(event: ReactPointerEvent<HTMLCanvasElement>) { drawing.current = true; const p = point(event); const context = ref.current!.getContext("2d")!; context.beginPath(); context.moveTo(p.x, p.y); event.currentTarget.setPointerCapture(event.pointerId); }
  function move(event: ReactPointerEvent<HTMLCanvasElement>) { if (!drawing.current) return; const p = point(event); const context = ref.current!.getContext("2d")!; context.lineWidth = 2; context.lineCap = "round"; context.strokeStyle = "#102532"; context.lineTo(p.x, p.y); context.stroke(); }
  function end() { drawing.current = false; changed(ref.current?.toDataURL("image/png")); }
  function clear() { const canvas = ref.current!; const context = canvas.getContext("2d")!; context.fillStyle = "#fff"; context.fillRect(0, 0, canvas.width, canvas.height); changed(undefined); }
  useEffect(clear, []);
  return <div className="signature-pad"><span>On-screen signature</span><canvas ref={ref} width={620} height={180} onPointerDown={start} onPointerMove={move} onPointerUp={end} /><button type="button" onClick={clear}>Clear signature</button>{value && <small>Signature captured</small>}</div>;
}

const label = (status: LoadPod["status"]) => ({ draft: "Draft", awaiting_review: "Awaiting manager review", changes_requested: "Changes requested", approved: "Approved — ready to send", sent: "Sent to poster" })[status];
